import { action, makeObservable } from 'mobx';
import {
    CoreFetch,
    DocumentApi,
    ProviderCreateDocumentType,
    SupportDocumentType,
} from '../api';
import { sanitizeFilename } from '../services/primitives/filename-utils';
import { createUploadAbortError } from '../ui/batch-document-upload/upload-utils';

export interface ICoreDocumentsStoreConfig {
    supportGrant?: boolean;
}

export interface UploadDocumentFileOptions {
    description?: string;
    documentType?: SupportDocumentType | ProviderCreateDocumentType;
    claimId?: string;
    signal?: AbortSignal;
    beforeRequest?: (signal?: AbortSignal) => Promise<void>;
}

const throwIfAborted = (signal?: AbortSignal): void => {
    if (signal?.aborted) {
        throw signal.reason ?? createUploadAbortError();
    }
};

const awaitBeforeRequest = async (options?: UploadDocumentFileOptions): Promise<void> => {
    if (!options?.beforeRequest) {
        return;
    }

    await options.beforeRequest(options.signal);
};

/** Reserve rate-limit capacity for CORS preflight (OPTIONS) plus POST. */
const awaitMutationRequest = async (options?: UploadDocumentFileOptions): Promise<void> => {
    await awaitBeforeRequest(options);
    await awaitBeforeRequest(options);
};

export class CoreDocumentsStore {
    private config?: ICoreDocumentsStoreConfig;

    constructor(config?: ICoreDocumentsStoreConfig) {
        makeObservable(this);
        this.config = config;
    }

    private get isSupportApp(): boolean {
        return Boolean(this.config?.supportGrant);
    }

    @action
    public setSupportGrant = (support: boolean | undefined): void => {
        const nextSupportGrant = Boolean(support);
        if (this.config?.supportGrant === nextSupportGrant) {
            return;
        }
        this.config = { ...(this.config || {}), supportGrant: nextSupportGrant };
    };

    private getDocumentApi = (signal?: AbortSignal): DocumentApi => {
        const docApi = CoreFetch<DocumentApi>(DocumentApi);

        if (!signal) {
            return docApi;
        }

        return docApi.withPreMiddleware(async context => ({
            url: context.url,
            init: { ...context.init, signal },
        }));
    };

    private uploadFileToS3 = (url: string, file: File, signal?: AbortSignal): Promise<void> => {
        throwIfAborted(signal);

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();

            const cleanup = (): void => {
                signal?.removeEventListener('abort', onAbort);
            };

            const onAbort = (): void => {
                xhr.abort();
                cleanup();
                reject(signal?.reason ?? createUploadAbortError());
            };

            signal?.addEventListener('abort', onAbort);

            xhr.addEventListener('load', () => {
                cleanup();
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                } else {
                    reject(new Error(`S3 upload failed with status ${xhr.status}`));
                }
            });
            xhr.addEventListener('error', () => {
                cleanup();
                reject(new Error('S3 upload failed'));
            });
            xhr.addEventListener('abort', () => {
                cleanup();
                reject(signal?.reason ?? createUploadAbortError());
            });
            xhr.open('PUT', url);
            xhr.setRequestHeader('x-amz-server-side-encryption', 'aws:kms');
            xhr.overrideMimeType(file.type);
            xhr.send(file);
        });
    };

    public uploadDocumentFile = async (file: File, options?: UploadDocumentFileOptions): Promise<string> => {
        const signal = options?.signal;
        throwIfAborted(signal);

        const docApi = this.getDocumentApi(signal);

        // 1) Create document
        const filename = sanitizeFilename(file.name);
        const description = options?.description || filename;
        const documentType =
            options?.documentType ||
            (this.isSupportApp ? SupportDocumentType.MESSAGEDOCUMENT : ProviderCreateDocumentType.MESSAGEDOCUMENT);

        await awaitMutationRequest(options);

        const created = this.isSupportApp
            ? await docApi.createSupportDocument({
                  supportCreateDocumentPayload: {
                      filename,
                      description,
                      documentType: documentType as SupportDocumentType,
                      claimId: options?.claimId,
                  },
              })
            : await docApi.createProviderDocument({
                  providerCreateDocumentPayload: {
                      filename,
                      documentType: documentType as ProviderCreateDocumentType,
                      description,
                      claimId: options?.claimId,
                  },
              });

        throwIfAborted(signal);

        if (!created?.id) throw new Error('Failed to create document');

        // 2) Create upload
        await awaitMutationRequest(options);

        const upload = this.isSupportApp
            ? await docApi.createSupportDocumentUpload({ id: created.id, body: {} })
            : await docApi.createProviderDocumentUpload({ id: created.id, body: {} });

        throwIfAborted(signal);

        if (!upload?.url) throw new Error('Failed to create document upload');

        // 3) PUT file to S3
        await this.uploadFileToS3(upload.url, file, signal);

        throwIfAborted(signal);

        // 4) Refresh metadata
        await awaitMutationRequest(options);

        if (this.isSupportApp) {
            await docApi.refreshDocumentMetadata({ id: created.id, body: {} });
        } else {
            await docApi.refreshProviderDocumentMetadata({ id: created.id, body: {} });
        }

        throwIfAborted(signal);

        // 5) Verify the uploaded file is available on the document record
        await awaitBeforeRequest(options);

        if (this.isSupportApp) {
            await docApi.getSupportDocument({ id: created.id });
        } else {
            await docApi.getProviderDocument({ id: created.id });
        }

        return created.id;
    };
}

export default CoreDocumentsStore;
